import {
  CreateCdfParams,
  GeckoBinnedDataBuffer,
  QuantitativeSummaryStatistics,
  useCdfRawValues,
  useCreateCustomCdf,
  useQuantitativeDataSummaryStats,
} from 'features/dataLayers'
import {
  FigureSelectionArea,
  RegionOfInterest,
  SelectedBins,
  selectionToBins,
} from 'features/fde'
import { useIndicationData } from 'features/indicationTriage/IndicationContext'
import { useRolodexCache } from 'features/rolodex'
import _ from 'lodash'
import { useId, useMemo } from 'react'
import {
  GeckoAggregation,
  GeckoComponentCoords,
  GeckoComponentDataFrame,
  GeckoComponentRect,
} from 'types'

function getCroppedCdfData(
  cdf: GeckoComponentDataFrame,
  binnedDataBuffer: GeckoBinnedDataBuffer,
  { minCorner: [minX, minY], maxCorner: [maxX, maxY] }: SelectedBins
): number[] {
  const binning = cdf.data.binning
  const binIndexes = new Set<number>()
  for (let x = minX; x < maxX; x++) {
    for (let y = minY; y < maxY; y++) {
      binIndexes.add(y * binning.x_bin_count + x)
    }
  }
  return _.sortBy([...binIndexes]).map(
    (index: number) => binnedDataBuffer.values.at(index) ?? Number.NaN
  )
}

function getCroppedBinning(
  oldBinning: GeckoComponentDataFrame['data']['binning'],
  { minCorner: [minX, minY], maxCorner: [maxX, maxY] }: SelectedBins
): GeckoComponentDataFrame['data']['binning'] {
  return {
    bin_size: oldBinning.bin_size,
    x_bin_count: maxX - minX,
    y_bin_count: maxY - minY,
    offset: oldBinning.offset,
  }
}

function getCroppedGeometry(
  componentID: string,
  selection: RegionOfInterest['selection']
): GeckoComponentDataFrame['data']['geometry'] {
  return GeckoComponentRect.CreateFromCoords(
    GeckoComponentCoords.CreateFromMeasurements(
      componentID,
      selection.x.start,
      selection.y.start
    ),
    GeckoComponentCoords.CreateFromMeasurements(
      componentID,
      selection.x.end,
      selection.y.end
    )
  )
}

function getCdfCreateParams(
  oldCdf: GeckoComponentDataFrame,
  croppedData: number[],
  relevantBins: SelectedBins,
  selection: RegionOfInterest['selection']
): CreateCdfParams {
  const croppedBinning = getCroppedBinning(oldCdf.data.binning, relevantBins)
  return {
    view: oldCdf.data.params.view,
    // This is a bit of a nasty hack, but we need to make sure that
    // our synthetic CDF doesn't get picked as the main thickness CDF,
    // so we swap out the aggregation
    aggregation:
      oldCdf.data.params.aggregation === GeckoAggregation.None
        ? GeckoAggregation.Mean
        : GeckoAggregation.None,
    algorithm: oldCdf.data.params.algorithm,
    anglePlotSetting: oldCdf.data.params.angle_plot_setting,
    binning: croppedBinning,
    geometry: getCroppedGeometry(oldCdf.data.geometry.componentID, selection),
    data: inflateDataTo2D(croppedData, croppedBinning.x_bin_count),
    measurementUnit: oldCdf.data.params.measurement_unit,
    source: oldCdf.data.params.source,
    tags: oldCdf.tags,
  }
}

export function inflateDataTo2D(data: number[], xBinCount: number): number[][] {
  const data2D: number[][] = new Array(Math.ceil(data.length / xBinCount))
  for (let y = 0; y < data2D.length; y++) {
    data2D[y] = new Array(xBinCount)
    for (let x = 0; x < xBinCount; x++) {
      data2D[y][x] = data[y * xBinCount + x]
    }
  }
  return data2D
}

const CROPPED_ID_SUFFIX = '-@@cropped'

export function isCroppedCdf(cdfId: string): boolean {
  return cdfId.endsWith(CROPPED_ID_SUFFIX)
}

export function useCroppedCdfSummaryStats(
  cdf: GeckoComponentDataFrame | undefined,
  selection: FigureSelectionArea | undefined
): {
  stats: QuantitativeSummaryStatistics | undefined
  croppedCdf: GeckoComponentDataFrame | undefined
  croppedDataAverage: number | undefined
  isLoading: boolean
} {
  const { buffer: binnedDataBuffer, isLoading: cdfDataLoading } =
    useCdfRawValues(cdf)
  const relevantBins = useMemo(
    () =>
      selection && cdf
        ? selectionToBins(selection, cdf?.data.binning, cdf?.data.geometry)
        : null,
    [selection, cdf]
  )
  const croppedData = useMemo(() => {
    if (!cdf || !binnedDataBuffer || !relevantBins) {
      return null
    }
    return getCroppedCdfData(cdf, binnedDataBuffer, relevantBins)
  }, [cdf, binnedDataBuffer, relevantBins])
  const rdCache = useRolodexCache()
  const createCdfParams = useMemo(
    () =>
      cdf && croppedData && relevantBins && selection
        ? getCdfCreateParams(cdf, croppedData, relevantBins, selection)
        : null,
    [cdf, croppedData, relevantBins, selection]
  )
  const hookId = useId()
  const croppedCdfId = useCreateCustomCdf(
    createCdfParams,
    rdCache,
    [hookId, cdf?.id ?? '', cdf?.generation ?? ''].join('.') + CROPPED_ID_SUFFIX
  )
  const { componentDataFrames } = useIndicationData()
  const croppedCdf = componentDataFrames.find((df) => df.id === croppedCdfId.id)
  const { data: cdfStats, isLoading: cdfStatsLoading } =
    useQuantitativeDataSummaryStats(croppedCdf)

  const croppedDataAverage = useMemo(() => {
    const filtered = croppedData?.filter(
      (dat) => dat !== undefined && !isNaN(dat)
    )
    if (!filtered) {
      return 0
    }
    return _.sum(filtered) / filtered?.length
  }, [croppedData])

  return useMemo(
    () => ({
      stats: cdfStats,
      isLoading: cdfStatsLoading || cdfDataLoading,
      croppedDataAverage,
      croppedCdf,
    }),
    [cdfDataLoading, cdfStats, cdfStatsLoading, croppedCdf, croppedDataAverage]
  )
}
